function renderCampaignTemplate(template, fallback, remaining, values = {}) {
  const tokens = { ...values, remaining: remaining ?? '' };
  const interpolate = (text) => String(text || '').replace(/\[([a-z_]+)\]/gi, (match, key) => tokens[key] ?? match);
  let message = interpolate(template);
  if (/\[[^\]]+\]/.test(message) && fallback && fallback !== template) {
    message = interpolate(fallback);
  }
  return message;
}

function campaignProgressState(cart, eligibleIds, eligibleSkus, tiers) {
  const quantity = (cart.items || []).reduce((total, item) => {
    const eligible = eligibleSkus.size
      ? eligibleSkus.has(String(item.sku || '').replace(/\s/g, '').toLowerCase())
      : eligibleIds.has(Number(item.variant_id));
    return total + (eligible ? Math.max(0, Number(item.quantity) || 0) : 0);
  }, 0);
  const max = tiers[tiers.length - 1].count;
  const current = [...tiers].reverse().find(tier => quantity >= tier.count);
  const next = tiers.find(tier => quantity < tier.count);
  return { quantity, max, current, next, remaining: next ? next.count - quantity : 0, width: Math.min(quantity / max, 1) * 100 };
}

class WorldCupCampaignProgress extends HTMLElement {
  connectedCallback() {
    this.eligible = new Set(JSON.parse(this.querySelector('[data-eligible-variants]').textContent || '[]').map(Number));
    this.eligibleSkus = new Set(JSON.parse(this.querySelector('[data-eligible-skus]')?.textContent || '[]').map(sku => String(sku).replace(/\s/g, '').toLowerCase()).filter(Boolean));
    this.tiers = JSON.parse(this.querySelector('[data-campaign-tiers]')?.textContent || '[{"count":2,"percent":5},{"count":4,"percent":15}]')
      .filter(tier => Number(tier.count) > 0 && Number(tier.percent) > 0)
      .sort((a, b) => a.count - b.count);
    if (!this.tiers.length) return;
    this.revision = 0;
    this.fill = this.querySelector('[data-progress-fill]');
    this.status = this.querySelector('[data-progress-status]');
    this.milestones = this.querySelectorAll('[data-tier]');
    this.track = this.querySelector('[data-progress-track]');
    this.variantSection = this.closest('.world-cup-campaign--variants');
    const configuredShowOffset = Number(this.dataset.showOffset);
    this.showOffset = Number.isFinite(configuredShowOffset) ? configuredShowOffset : 50;
    this.onViewportChange = () => {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.updateVisibility();
      });
    };
    this.onCartEvent = (event) => {
      this.revision += 1;
      this.controller?.abort();
      if (Array.isArray(event.detail?.cart?.items)) this.render(event.detail.cart);
      else this.refresh();
    };
    this.cartEvents = ['on:cart:add', 'on:line-item:change', 'on:cart:change', 'volume-discount:cart-state', 'cart:updated', 'cart:change', 'shopify:cart:updated'];
    this.cartEvents.forEach((name) => {
      document.addEventListener(name, this.onCartEvent);
    });
    window.addEventListener('scroll', this.onViewportChange, { passive: true });
    window.addEventListener('resize', this.onViewportChange);
    this.drawer = document.getElementById('cart-drawer');
    if (this.drawer && typeof MutationObserver !== 'undefined') {
      this.drawerObserver = new MutationObserver(this.onViewportChange);
      this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    }
    if (this.dataset.pageScope === 'halloween' && typeof ResizeObserver !== 'undefined') {
      this.sizeObserver = new ResizeObserver(() => document.documentElement.style.setProperty('--halloween-progress-height', `${this.offsetHeight}px`));
      this.sizeObserver.observe(this);
    }
    this.updateVisibility();
    this.refresh();
  }

  disconnectedCallback() {
    this.revision += 1;
    this.controller?.abort();
    this.drawerObserver?.disconnect();
    this.sizeObserver?.disconnect();
    (this.cartEvents || []).forEach((name) => {
      document.removeEventListener(name, this.onCartEvent);
    });
    window.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
    if (this.visibilityFrame) cancelAnimationFrame(this.visibilityFrame);
  }

  updateVisibility() {
    const isVisible = this.drawer?.getAttribute('aria-hidden') !== 'false' && (!this.variantSection
      || this.variantSection.getBoundingClientRect().top <= window.innerHeight - this.showOffset);
    this.classList.toggle('is-visible', isVisible);
    this.setAttribute('aria-hidden', String(!isVisible));
  }

  async refresh() {
    const revision = ++this.revision;
    this.controller?.abort();
    this.controller = new AbortController();
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' }, signal: this.controller.signal });
      if (!response.ok) return;
      const cart = await response.json();
      if (this.isConnected && revision === this.revision) this.render(cart);
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Campaign cart progress could not be updated.', error);
    }
  }

  render(cart) {
    const state = campaignProgressState(cart, this.eligible, this.eligibleSkus, this.tiers);
    const { quantity, max, current, next, remaining } = state;
    this.fill.style.width = `${state.width}%`;
    this.milestones.forEach((marker) => marker.classList.toggle('is-earned', quantity >= Number(marker.dataset.tier)));

    const tokens = { current_percent: current?.percent || 0, next_percent: next?.percent || 0 };
    let message;
    if (!next) {
      message = renderCampaignTemplate(this.dataset.tierFour, this.dataset.tierFourFallback, 0, tokens);
    } else if (current) {
      message = renderCampaignTemplate(this.dataset.tierTwo, this.dataset.tierTwoFallback, remaining, tokens);
    } else {
      message = renderCampaignTemplate(this.dataset.beforeTierTwo, this.dataset.beforeTierTwoFallback, remaining, tokens);
    }
    this.status.textContent = message;
    this.track?.setAttribute('aria-valuemax', String(max));
    this.track?.setAttribute('aria-valuenow', String(Math.min(quantity, max)));
    this.track?.setAttribute('aria-valuetext', message);
  }
}

class WorldCupColorGroups extends HTMLElement {
  connectedCallback() {
    this.singleGroup = this.dataset.filterMode === 'single';
    this.buttons = [...this.querySelectorAll('[data-color-filter]')];
    this.groups = [...this.querySelectorAll('[data-color-group]')];
    this.activeFilter = this.singleGroup ? (this.groups[0]?.dataset.colorGroup || '') : 'all';
    this.setFilter(this.activeFilter);

    this.onClick = (event) => {
      const button = event.target.closest('[data-color-filter]');
      if (!button || !this.contains(button)) return;

      const requestedFilter = button.dataset.colorFilter;
      const nextFilter = !this.singleGroup && requestedFilter !== 'all' && requestedFilter === this.activeFilter
        ? 'all'
        : requestedFilter;
      this.setFilter(nextFilter);
    };

    this.onBlockSelect = (event) => {
      if (this.groups.some((group) => group.dataset.colorGroup === event.detail.blockId)) {
        this.setFilter(event.detail.blockId);
      }
    };

    this.addEventListener('click', this.onClick);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  setFilter(filter) {
    this.activeFilter = filter;
    this.buttons.forEach((button) => {
      const isActive = button.dataset.colorFilter === filter;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });
    this.groups.forEach((group) => {
      group.hidden = filter !== 'all' && group.dataset.colorGroup !== filter;
    });
  }
}

if (!customElements.get('world-cup-campaign-progress')) {
  customElements.define('world-cup-campaign-progress', WorldCupCampaignProgress);
}

if (!customElements.get('world-cup-color-groups')) {
  customElements.define('world-cup-color-groups', WorldCupColorGroups);
}
